/*
arbre-attrs.json loader — sidecar PWA généré pull-time par
`scripts/sync-assets.mjs` à partir du DB Room pré-baked Android
(`databases/arbres-paris.db`). Comble un gap data structurel : la
geojson PWA n'expose que `{id, sk, remarquable}`, mais plusieurs
surfaces ont besoin de l'arrondissement et des dimensions de chaque
arbre capturé.

Format compact v3 (cf. `sync-assets.mjs:emitArrSidecar`) :

	{
	  "version": 3,
	  "slugs": ["1", ..., "20", "vincennes", "boulogne", "other"],
	  "addrs": ["116 RUE DE GRENELLE, PARIS 7E ARRDT", ...],
	  "byId": { "100841": [4, 12, 75, 1832], "100842": [4, null, null, 1832], ... }
	}

Tuple par arbre = `[arrSlugIdx, hauteurM | null, circCm | null,
addrIdx | null]`. null représente une mesure manquante dans le CSV
OpenData (fréquent pour les arbres jeunes ou récemment plantés ;
~21k arbres sans hauteur et ~17k sans circonférence sur 217k).
`addrs` (depuis v3) = table dédupliquée des
adresses complètes (~32k distinctes pour 217k arbres) — les entrées de
la Map partagent les références de chaînes, pas de copie par arbre.

Consommateurs :
- `badgeEvaluator.ts` : Familier du [arr] + Géant / Bonsaï / Vieux sage /
  Jeune pousse.
- `ProfileScreen.ts` : barres 6 (Arrondissements visités) et 7
  (Arrondissements complétés).
- `MapScreen.ts` : sheet détail arbre, lignes hauteur + circ + adresse.
- `RemarquablesScreen.ts` / `RemarquableDetailScreen.ts` /
  `SpeciesScreen.ts` : adresse des remarquables.

Payload typique : ~217k entries, ~7 MB raw, ~1.3 MB gzipped (SW
cache-first sur `/data/*`). Singleton lazy mémoïsant une Map pour
éviter les allocations à chaque lookup.
*/

import { fetchJson } from './fetchJson'
import { arrKeyFromSlug, type ArrKey } from './arrKey'

const kAssetPath = '/data/arbre-attrs.json'
// v3 = adresses ; v2 (sans `addrs`) reste accepté pour la transition —
// le SW data est stale-while-revalidate, un client peut servir l'ancien
// fichier une dernière session (adresse alors null, fallback arr court).
const kSupportedVersions: readonly number[] = [2, 3]

type RawTuple = [number, number | null, number | null, (number | null)?]

interface RawSidecar
{
	version: number
	slugs: string[]
	addrs?: string[]  // absent en v2
	byId: Record<string, RawTuple>
}

export interface ArbreAttrs
{
	arr: ArrKey
	heightM: number | null  // hauteur en mètres entiers (CSV OpenData), null si absent
	circCm: number | null  // circonférence en centimètres entiers, null si absent
	adresse: string | null  // adresse complète OpenData (« 116 RUE DE GRENELLE, PARIS 7E ARRDT »), null si vide
}

let fAttrs: Map<number, ArbreAttrs> | null = null  // memoized arbreId -> attrs
let fLoading: Promise<void> | null = null  // dedup concurrent callers

/*
Ensure the sidecar is loaded. Idempotent. Builds the Map in one pass,
dropping the raw JSON immediately after — peak memory ~15-20 MB during
load, steady-state ~8-12 MB (217k entries × Map overhead + ArrKey
objects).
*/
export async function ensureArbreAttrs(): Promise<void>
{
	if (fAttrs) return
	if (fLoading) return fLoading
	fLoading = (async () => {
		const vRaw = await fetchJson<RawSidecar>(kAssetPath)
		if (!kSupportedVersions.includes(vRaw.version))
		{
			throw new Error(`${kAssetPath}: unsupported version ${vRaw.version} (expected ${kSupportedVersions.join('/')})`)
		}
		// Pre-resolve slugs → ArrKey once (cheap reuse across 217k entries).
		const vKeysBySlugIndex: (ArrKey | null)[] = vRaw.slugs.map(arrKeyFromSlug)
		const vAddrs = vRaw.addrs ?? []
		const vMap = new Map<number, ArbreAttrs>()
		for (const [vIdStr, vTuple] of Object.entries(vRaw.byId))
		{
			const vKey = vKeysBySlugIndex[vTuple[0]]
			if (!vKey) continue  // skip unknown slug index (shouldn't happen)
			const vAddrIdx = vTuple[3] ?? null
			vMap.set(parseInt(vIdStr, 10), {
				arr: vKey,
				heightM: vTuple[1],
				circCm: vTuple[2],
				adresse: vAddrIdx !== null ? vAddrs[vAddrIdx] ?? null : null,
			})
		}
		fAttrs = vMap
		fLoading = null
	})()
	return fLoading
}

/*
Arr lookup. Returns null when the sidecar is not loaded yet or when the
arbre is unknown (orphan capture from an older dataset). Caller decides
whether to fall back to `{kind:'other'}` or to skip.
*/
export function arrFor(inArbreId: number): ArrKey | null
{
	const vAttrs = fAttrs?.get(inArbreId)
	return vAttrs?.arr ?? null
}

/*
Hauteur en mètres entiers (1..30+), ou null si la mesure manque. Le
badge Géant déclenche à `> 30`, le Bonsaï à `< 2`.
*/
export function heightMFor(inArbreId: number): number | null
{
	const vAttrs = fAttrs?.get(inArbreId)
	return vAttrs?.heightM ?? null
}

/*
Circonférence en centimètres entiers, ou null si la mesure manque. Le
badge Vieux sage déclenche à `> 400`, la Jeune pousse à `< 10`.
*/
export function circCmFor(inArbreId: number): number | null
{
	const vAttrs = fAttrs?.get(inArbreId)
	return vAttrs?.circCm ?? null
}

/*
Combined accessor — utile au MapScreen sheet détail qui rend 3 lignes
d'un coup, sans 3 lookups successifs.
*/
export function attrsFor(inArbreId: number): ArbreAttrs | null
{
	return fAttrs?.get(inArbreId) ?? null
}
